import React, { MouseEvent } from "react";
import htmlParse, { HTMLReactParserOptions } from "html-react-parser";
import FlexBox from "../../../FlexBox/FlexBox";
import {
  _LandingHighlighProductContainer,
  _LandingHighlighProductAdrienCachotRoot,
  _LandingHighlighProductContentContainer,
} from "./LandingHighlighProductAdrienCachot.styled";
import Typography from "../../../../Foundation/Typography/Typography";
import CompositionItem from "../CompositionItem/CompositionItem";
import Divider from "../../../Divider/Divider";
import useBreakpoint from "../../../Grid/hooks/useBreakpoint";
import getMobileOperatingSystem from "../../../../Utils/getMobileOperatingSystem";
import {
  ICompositionVariant,
  IProductComposition,
} from "../LandingHighlighProduct";
import { adrienCachotImages } from "../../../../Assets/images/adrien-cachot";
import StyledDiv from "../../../StyledDiv/StyledDiv";
import BoxBackgroundImage from "./BoxBackgroundImage/BoxBackgroundImage";
import GradientButton from "../../../../Foundation/Button/GradientButton";
import useOnScreen from "../../../../Hooks/useOnScreen";

interface ILandingHighlighProductAdrienCachotProps {
  title: string;
  highlitedTitle?: string;
  description?: string;
  mentionDesktop?: string;
  ctaLabel: string;
  onClick?: () => void;
  compositions?: ICompositionVariant;
  compositionLabel?: string;
  price?: string;
  priceSubtitle?: string;
  product_display_from_mention?: string;
  product_price_vae?: string;
  product_price_vad?: string;
  vaeLabel?: string;
  vadLabel?: string;
  imageId?: string;
  hideCta?: boolean;
}

const LandingHighlighProductAdrienCachot = (
  props: ILandingHighlighProductAdrienCachotProps
) => {
  const {
    title,
    description,
    mentionDesktop,
    ctaLabel = "Je découvre",
    onClick,
    compositionLabel,
    compositions,
    price,
    priceSubtitle,
    product_display_from_mention,
    product_price_vae,
    product_price_vad,
    vaeLabel,
    vadLabel,
    imageId,
    hideCta = false,
  } = props;

  const { mobile } = useBreakpoint();
  const os = getMobileOperatingSystem();
  const [containerRef, isOnScreen] = useOnScreen<HTMLDivElement>(
    { root: null, rootMargin: "200px 0px", threshold: 0 },
    { ignoreReverse: true }
  );

  const handleHashLinkClick = (e: MouseEvent, href: string) => {
    e.preventDefault();
    const targetId = href.replace("#", "");
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
      const targetRect = targetElement.getBoundingClientRect();
      let targetPosition = window.scrollY + targetRect.top - 64;
      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }
  };

  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (
        "attribs" in domNode &&
        domNode.name === "a" &&
        domNode.attribs?.href?.startsWith("#")
      ) {
        const { href } = domNode.attribs;
        return React.createElement(
          "a",
          {
            href,
            onClick: (e: MouseEvent) => handleHashLinkClick(e, href),
            style: {
              cursor: "pointer",
              color: "white",
              textDecoration: "underline",
            },
          },
          domNode.children &&
            domNode.children[0] &&
            "data" in domNode.children[0]
            ? (domNode.children[0] as { data: string }).data
            : ""
        );
      }
    },
  };

  const renderCompositions = () => {
    if (!compositions) return null;
    if (compositions.type === "box") {
      return (
        <FlexBox direction="column" gap={4} className="composition-list">
          {compositions.list.map((item: IProductComposition) => (
            <CompositionItem
              theme="adriencachot"
              key={item.id_product}
              product={item}
            />
          ))}
        </FlexBox>
      );
    }
    if (compositions.type === "paragraph") {
      return (
        <Typography variant="subtitle2" color="white">
          {compositions.text}
        </Typography>
      );
    }
    return null;
  };

  return (
    <>
      <_LandingHighlighProductAdrienCachotRoot $os={os} ref={containerRef}>
        <StyledDiv
          as={BoxBackgroundImage}
          imageId={imageId}
          className="highlight-product-adrien-cachot-background-image"
          bg={
            isOnScreen
              ? {
                  desktop: adrienCachotImages.bgInnovationHalo,
                  mobile: adrienCachotImages.bgInnovationHalo,
                }
              : {}
          }
          styles={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        />
        <_LandingHighlighProductContentContainer $os={os}>
          <_LandingHighlighProductContainer
            className="highlight-product-adrien-cachot-container"
            style={{ position: "relative" }}
          >
            {/* top content */}
            <StyledDiv
              as={FlexBox}
              direction="column"
              gap={12}
              className="highlight-product-adrien-cachot-top-content"
              styles={{
                maxWidth: 286,
                position: "relative",
                zIndex: 2,
                mobile: {
                  maxWidth: 291,
                  textAlign: "left",
                },
              }}
            >
              <StyledDiv as={Typography} variant="adriencachot" color="white">
                {title}
              </StyledDiv>
              {description && (
                <StyledDiv
                  as={Typography}
                  variant="adriencachotBody1"
                  color="white"
                >
                  {htmlParse(description + "", parserOptions)}
                </StyledDiv>
              )}
            </StyledDiv>

            {/* bottom content */}
            <StyledDiv
              as={FlexBox}
              styles={{
                position: "relative",
                zIndex: 2,
                mobile: {
                  justifyContent: "flex-start",
                },
              }}
              className="highlight-product-adrien-cachot-bottom-content"
            >
              <StyledDiv
                as={FlexBox}
                className="highlight-product-adrien-cachot-bottom-content-wrapper"
                direction="column"
                styles={{
                  gap: 24,
                  mobile: {
                    textAlign: "left",
                    gap: 12,
                  },
                }}
              >
                {(compositions || compositionLabel) && (
                  <FlexBox
                    mt={32}
                    direction="column"
                    gap={mobile ? 8 : 18}
                    className="composition-container"
                  >
                    {compositionLabel && (
                      <Typography
                        variant="adriencachotBody2"
                        color="white"
                        style={{ textAlign: mobile ? "left" : undefined }}
                      >
                        {compositionLabel}
                      </Typography>
                    )}
                    {renderCompositions()}
                  </FlexBox>
                )}

                {/* price */}
                {price && (
                  <FlexBox mt={8} gap={10} className="price-container">
                    {product_display_from_mention && (
                      <Typography variant="adriencachotBody2" color="white">
                        {product_display_from_mention}
                      </Typography>
                    )}
                    <Typography variant="adriencachotBody2" color="white">
                      {price}
                    </Typography>
                    {priceSubtitle && (
                      <>
                        <Typography
                          variant="adriencachotBody2"
                          color="greyText2"
                        >
                          {"/"} {priceSubtitle}
                        </Typography>
                      </>
                    )}
                  </FlexBox>
                )}

                {/* VAE/VAD prices */}
                {(product_price_vae || product_price_vad) && (
                  <FlexBox gap={10} className="price-container">
                    {product_price_vae && vaeLabel && (
                      <Typography variant="adriencachotBody2" color="white">
                        {vaeLabel} {product_price_vae}
                      </Typography>
                    )}
                    {product_price_vae && product_price_vad && (
                      <Divider direction="vertical" weight={2} />
                    )}
                    {product_price_vad && vadLabel && (
                      <Typography variant="adriencachotBody2" color="white">
                        {vadLabel} {product_price_vad}
                      </Typography>
                    )}
                  </FlexBox>
                )}

                {!hideCta && (
                  <div>
                    <GradientButton
                      className="landing-action-button"
                      onClick={onClick}
                    >
                      {ctaLabel}
                    </GradientButton>
                  </div>
                )}
              </StyledDiv>
            </StyledDiv>
          </_LandingHighlighProductContainer>
        </_LandingHighlighProductContentContainer>
      </_LandingHighlighProductAdrienCachotRoot>
    </>
  );
};

export default LandingHighlighProductAdrienCachot;
